import { useContext, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ModalContext } from '../context/ModalProvider';
import Product from './Product';
import OptimizedImg from '../components/OptimizedImg';
import { incrementSession, trackSeriesClicked, trackSeriesDetailOpened, trackCategoryClick, toSeriesFamilyName } from '../utils/analytics';

function hasPackages(item) {
    return item.ProductList?.length > 0 || item.BMKGroups?.length > 0;
}

// A base model with no packages of its own is a dead end — clicking it would
// just re-fetch /api/Products/{id} directly, which for a base model comes
// back without its packages embedded (they only exist nested inside the
// parent listing that got us here), crashing the next page the same way
// loadProducts's flattening (see router/Router.js) already avoids recursing
// into these.
function isDeadEndModel(item) {
    return item.BaseModel === true && !hasPackages(item);
}

export default function ProductList({ products }) {
    const { productSlug, seriesSlug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Auto-skip when there's exactly one intermediate group (not a final
    // product, and not a dead-end model — see isDeadEndModel above; skipping
    // into one of those would hit the same childless-refetch crash).
    useEffect(() => {
        if (products.length === 1) {
            const only = products[0];
            if (!hasPackages(only) && !isDeadEndModel(only)) {
                let url = only.Id + '-' + encodeURIComponent(only.Name);
                if (productSlug) {
                    url = '/' + encodeURIComponent(productSlug.trim()) + '/' + url;
                }
                navigate(url, { replace: true });
            }
        }
    }, [products, productSlug, navigate]);

    const isModelList = products.length > 0 && products[0].ProductList && products[0].ProductList.length > 0;

    // Dead-end models have nothing to show past this point — leave them out
    // of the grid entirely rather than a card that goes nowhere.
    const visibleProducts = products.filter((product) => !isDeadEndModel(product));

    return (
        <div id="product_groups_container_content" className={isModelList ? 'groupContainerContent modelList' : 'groupContainerContent'}>
            {products.length === 0 ? (
                <div className="loader">Loading...</div>
            ) : visibleProducts.length === 0 ? (
                <NoAvailableSeries productSlug={productSlug} seriesSlug={seriesSlug} navigate={navigate} isFirstEntry={location.key === 'default'} />
            ) : (
                visibleProducts.map((product, index) => {
                    if (hasPackages(product)) {
                        return <Product key={product.Id} product={product} />
                    }
                    return <ProductGroup key={product.Id} productGroup={product} position={index + 1} />
                })
            )}
        </div>
    );
}

function NoAvailableSeries({ productSlug, seriesSlug, navigate, isFirstEntry }) {
    const goBack = () => {
        if (!isFirstEntry) {
            window.history.back();
        } else if (seriesSlug && productSlug) {
            navigate('/' + encodeURIComponent(productSlug.trim()));
        } else {
            navigate('/');
        }
    };

    return (
        <div className="noAvailableSeries">
            <p>There is no available Series for your selection.</p>
            <button className="button orange-button" onClick={goBack}>Back</button>
        </div>
    );
}

function ProductGroup({ productGroup, position }) {
    const { productSlug } = useParams();
    let url = productGroup.Id + '-' + encodeURIComponent(productGroup.Name);
    if (productSlug) {
        url = '/' + encodeURIComponent(productSlug.trim()) + '/' + url;
    }

    if (productGroup.IsProductSeries && !productGroup.Name.includes('Series')) {
        productGroup.Name += ' Series';
    }

    const categoryName = productSlug
        ? decodeURIComponent(productSlug.substring(productSlug.indexOf('-') + 1))
        : undefined;

    // Analytics-only: some series groups are named by their concatenated
    // model codes rather than a friendly family name — see toSeriesFamilyName.
    const seriesFamilyName = toSeriesFamilyName(productGroup.Name);

    const sendEvent = () => {
        if (productSlug) {
            const count = incrementSession('session_series_clicks');
            trackSeriesClicked({
                series_name: seriesFamilyName,
                click_position: position,
                category_name: categoryName,
                session_series_clicks: count,
            });
        } else {
            incrementSession('session_category_clicks');
            trackCategoryClick(productGroup.Name);
        }
    };

    let highlightedUses = [];
    if (productGroup.HighlightedUses !== null) {
        highlightedUses = Object.values(productGroup.HighlightedUses).filter(use => use.Key);
    }

    const modal = useContext(ModalContext);
    const showDetails = (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (productSlug) {
            const count = incrementSession('details_opened_in_session');
            trackSeriesDetailOpened({ series_name: seriesFamilyName, details_opened_in_session: count });
        }

        modal.show(
            <div className="highlights-container">
                <h2 className="modal-title">{productGroup.Name} Details</h2>
                <div className="modal-image">
                    <img src={productGroup.ImageUrl} loading="lazy" />
                </div>
                <div className="modal-highlights">
                    <h2>Highlighted Uses</h2>
                    <ul>
                        {highlightedUses.map(use => (
                            <li key={use.Key}>
                                <h3>{use.KeyAlias}</h3>
                                <p>{use.Value}</p>
                            </li>
                        ))}
                    </ul>
                </div>
                <Link to={url} className="action-button" onClick={() => modal.show()}>Choose {productGroup.Name}</Link>
                <button onClick={() => modal.show()} className="action-button cancel">Back</button>
            </div>
        );
    };

    const searchRegExp = /[\/]/g;
    const replaceWith = ' / ';

    return (
        <Link
            to={url}
            onClick={sendEvent}
            className="productGroupLink groupLink eligible"
            data-series-name={productGroup.Name}
            data-series-position={position}
            data-category-name={categoryName}
        >
            {highlightedUses.length > 0 && (
                <span
                    className="detailsLink"
                    onClick={showDetails}
                    data-series-name={productGroup.Name}
                ><em className="bi bi-info-circle"></em> Details</span>
            )}
            <span className="groupThumbContainer">
                <OptimizedImg src={productGroup.ImageUrl} width={600} className="groupThumb thumb" alt={productGroup.Name} loading="lazy" />
            </span>
            <span className="headline">{productGroup.Name.replace(searchRegExp, replaceWith)}</span>
        </Link>
    );
}
